/**
 * Verbatim port of the AngularJS 1.x `numberFilter` / `formatNumber` / `roundNumber` / `parse`
 * (node_modules/angular/angular.js) for the en-US `$locale` (the frozen baseline's `$filter('number')`).
 * The constants are the en-us `NUMBER_FORMATS.PATTERNS[0]` + `GROUP_SEP`/`DECIMAL_SEP`. Ported
 * character-for-character (incl. the half-up `digit >= 5` rounding, the big-number exponent path,
 * the negative-zero guard) so native `number` consumers format identically to the baseline off the
 * `$filter('number')` bridge. Proven by account-parity/_diag-number-check.mjs
 * (LIVE `$filter('number')` vs this, 0 mismatches across the value/fractionSize domain).
 */
const MAX_DIGITS = 22;
const DECIMAL_SEP = '.';
const GROUP_SEP = ',';
const ZERO_CHAR = '0';
const PATTERN = { minInt: 1, minFrac: 0, maxFrac: 3, gSize: 3, lgSize: 3, negPre: '-', negSuf: '', posPre: '', posSuf: '' };

interface ParsedNumber {
  d: number[];
  e: number;
  i: number;
}

function parse(numStr: string): ParsedNumber {
  let exponent = 0;
  let digits: number[];
  let numberOfIntegerDigits: number;
  let i: number, j: number, zeros: number;

  if ((numberOfIntegerDigits = numStr.indexOf(DECIMAL_SEP)) > -1) {
    numStr = numStr.replace(DECIMAL_SEP, '');
  }

  if ((i = numStr.search(/e/i)) > 0) {
    if (numberOfIntegerDigits < 0) numberOfIntegerDigits = i;
    numberOfIntegerDigits += +numStr.slice(i + 1);
    numStr = numStr.substring(0, i);
  } else if (numberOfIntegerDigits < 0) {
    numberOfIntegerDigits = numStr.length;
  }

  for (i = 0; numStr.charAt(i) === ZERO_CHAR; i++) {
    /* empty */
  }

  if (i === (zeros = numStr.length)) {
    digits = [0];
    numberOfIntegerDigits = 1;
  } else {
    zeros--;
    while (numStr.charAt(zeros) === ZERO_CHAR) zeros--;

    numberOfIntegerDigits -= i;
    digits = [];
    for (j = 0; i <= zeros; i++, j++) {
      digits[j] = +numStr.charAt(i);
    }
  }

  if (numberOfIntegerDigits > MAX_DIGITS) {
    digits = digits.splice(0, MAX_DIGITS - 1);
    exponent = numberOfIntegerDigits - 1;
    numberOfIntegerDigits = 1;
  }

  return { d: digits, e: exponent, i: numberOfIntegerDigits };
}

function roundNumber(parsedNumber: ParsedNumber, fractionSize: any, minFrac: number, maxFrac: number): void {
  const digits = parsedNumber.d;
  let fractionLen = digits.length - parsedNumber.i;

  const fs = fractionSize === undefined ? Math.min(Math.max(minFrac, fractionLen), maxFrac) : +fractionSize;

  let roundAt = fs + parsedNumber.i;
  const digit = digits[roundAt];

  if (roundAt > 0) {
    digits.splice(Math.max(parsedNumber.i, roundAt));
    for (let j = roundAt; j < digits.length; j++) {
      digits[j] = 0;
    }
  } else {
    fractionLen = Math.max(0, fractionLen);
    parsedNumber.i = 1;
    digits.length = Math.max(1, (roundAt = fs + 1));
    digits[0] = 0;
    for (let i = 1; i < roundAt; i++) digits[i] = 0;
  }

  if (digit >= 5) {
    if (roundAt - 1 < 0) {
      for (let k = 0; k > roundAt; k--) {
        digits.unshift(0);
        parsedNumber.i++;
      }
      digits.unshift(1);
      parsedNumber.i++;
    } else {
      digits[roundAt - 1]++;
    }
  }

  for (; fractionLen < Math.max(0, fs); fractionLen++) digits.push(0);

  const carry = digits.reduceRight(function (carry: number, d: number, i: number, digits: number[]) {
    d = d + carry;
    digits[i] = d % 10;
    return Math.floor(d / 10);
  }, 0);
  if (carry) {
    digits.unshift(carry);
    parsedNumber.i++;
  }
}

function formatNumber(numberArg: any, fractionSize: any): string {
  // Alias to an untyped local so the typeof guard narrows `numberArg` (not `number`), keeping
  // `number` as `any` for isNaN/isFinite/Math.abs/`< 0` (which rely on AngularJS's JS coercion).
  const number: any = numberArg;
  if (!(typeof numberArg === 'string' || typeof numberArg === 'number') || isNaN(number)) return '';

  const isInfinity = !isFinite(number);
  let isZero = false;
  const numStr = Math.abs(number) + '';
  let formattedText = '';
  let parsedNumber: ParsedNumber;

  if (isInfinity) {
    formattedText = '∞';
  } else {
    parsedNumber = parse(numStr);

    roundNumber(parsedNumber, fractionSize, PATTERN.minFrac, PATTERN.maxFrac);

    let digits = parsedNumber.d;
    let integerLen = parsedNumber.i;
    const exponent = parsedNumber.e;
    let decimals: number[] = [];
    isZero = digits.reduce(function (isZero: boolean, d: number) {
      return isZero && !d;
    }, true);

    while (integerLen < 0) {
      digits.unshift(0);
      integerLen++;
    }

    if (integerLen > 0) {
      decimals = digits.splice(integerLen, digits.length);
    } else {
      decimals = digits;
      digits = [0];
    }

    const groups: string[] = [];
    if (digits.length >= PATTERN.lgSize) {
      groups.unshift(digits.splice(-PATTERN.lgSize, digits.length).join(''));
    }
    while (digits.length > PATTERN.gSize) {
      groups.unshift(digits.splice(-PATTERN.gSize, digits.length).join(''));
    }
    if (digits.length) {
      groups.unshift(digits.join(''));
    }
    formattedText = groups.join(GROUP_SEP);

    if (decimals.length) {
      formattedText += DECIMAL_SEP + decimals.join('');
    }

    if (exponent) {
      formattedText += 'e+' + exponent;
    }
  }

  if (number < 0 && !isZero) {
    return PATTERN.negPre + formattedText + PATTERN.negSuf;
  } else {
    return PATTERN.posPre + formattedText + PATTERN.posSuf;
  }
}

/** Verbatim AngularJS en-US `numberFilter`: null/undefined pass through, else `formatNumber`. */
export function angularNumberFilter(value: any, fractionSize?: any): any {
  return value == null ? value : formatNumber(value, fractionSize);
}
